import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';

const PREVIEW = 220;
const GAP = 12;

/** Up to two initials, so a candidate without a usable photo still has a face. */
const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('') || '?';

/**
 * A candidate's photo, enlarging on hover.
 *
 * The avatar keeps its size whether or not the image loads. Records carried
 * over from the old app name a photo file that was never copied across, so
 * hiding the broken image would collapse the cell and make a long list visibly
 * reflow as each request fails — the placeholder holds the space instead.
 *
 * The enlarged copy is drawn into a portal on document.body rather than scaled
 * in place: the tables sit inside overflow containers and the dialogs inside a
 * stacking context, either of which would clip a plain CSS transform.
 */
const Photo = ({ src, size = 32, name = '' }) => {
  const [box, setBox] = useState(null);
  const [broken, setBroken] = useState(false);

  const usable = src && !broken;

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    // Sit to the right of the avatar, flipping left near the window edge, and
    // keep the whole preview on screen vertically.
    const spillsRight = rect.right + GAP + PREVIEW > window.innerWidth;
    const left = spillsRight ? Math.max(GAP, rect.left - GAP - PREVIEW) : rect.right + GAP;
    const top = Math.min(
      Math.max(GAP, rect.top + rect.height / 2 - PREVIEW / 2),
      window.innerHeight - PREVIEW - GAP
    );

    setBox({ left, top });
  }, []);

  const hide = useCallback(() => setBox(null), []);

  const frame = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (!usable) {
    return (
      <span
        style={{
          ...frame,
          background: '#e9ecef',
          color: '#868e96',
          fontSize: Math.round(size * 0.38),
          fontWeight: 600,
          letterSpacing: '0.02em',
        }}
        title={name || undefined}
      >
        {initialsOf(name)}
      </span>
    );
  }

  return (
    <>
      <span style={{ ...frame, cursor: 'zoom-in' }} onMouseEnter={show} onMouseLeave={hide}>
        <img
          src={src}
          alt={name}
          width={size}
          height={size}
          style={{ width: size, height: size, objectFit: 'cover' }}
          onError={() => { setBroken(true); setBox(null); }}
        />
      </span>

      {box && ReactDOM.createPortal(
        <img
          src={src}
          alt=""
          style={{
            position: 'fixed',
            left: box.left,
            top: box.top,
            width: PREVIEW,
            height: PREVIEW,
            objectFit: 'cover',
            borderRadius: '50%',
            border: '4px solid #fff',
            boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
            background: '#fff',
            zIndex: 20000,
            pointerEvents: 'none',
          }}
        />,
        document.body
      )}
    </>
  );
};

export default Photo;
